
















































import { computed, defineComponent, PropType, ref, toRefs } from '@vue/composition-api';
import { cloneDeep } from 'lodash-es';
import { randomString } from '@tt4b/ttam-utils';
import { parseUrl } from 'query-string';
import { useRequest, useGetClickIdBlackListApi, useInputTooltipShow, useInjectAbTest } from '@tt4b/ttam-hooks';

const TTCLID = 'ttclid';
const TTCLID_MACRO = '__CLICKID__';
const TTCLID_COMMON = 'E.C.P.Preview_';

const inputMinWidth = 342;
const randomClickIdLength = 60;

const name = 'ttam-creative-website-url-input';
export default defineComponent({
  name,
  props: {
    value: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    recommendUrls: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { recommendUrls } = toRefs(props);
    const handleFetchSuggestions = (queryString, callback) => {
      const urls = recommendUrls.value || [];
      const suggestions = urls.map(item => ({ value: item }));
      callback(suggestions);
    };
    const urlInputKey = ref(1);
    const __externalUrl = computed({
      get: () => props.value,
      set: v => {
        emit('change', v);
        emit('input', v);
      },
    });

    const {
      isShowInputTooltip,
      handleBlur: onInputTooltipBlur,
      handleFocus,
    } = useInputTooltipShow({
      url: __externalUrl,
    });

    const { run: checkClickID, isLoading } = useRequest(useGetClickIdBlackListApi, null, {
      manual: true,
    });

    const validateUrl = (urlString: string): boolean => {
      const reg = new RegExp(/^(?:http(s)?:\/\/)([\w-]+\.)+[\w-]+([\w-./?%&=]*)?/);

      return reg.test(urlString);
    };

    // 空值和非法链接要禁用Preview按钮
    const disabledPreview = computed(() => {
      if (!__externalUrl.value) {
        return true;
      }
      return !validateUrl(__externalUrl.value);
    });

    const { isInputUrlIterationTooltipTest } = useInjectAbTest();
    const isShowToolTip = computed(() => isShowInputTooltip.value && isInputUrlIterationTooltipTest.value);

    const replaceUrlParam = (urlString: string): string => {
      const paramName = TTCLID;
      const replacedVal = TTCLID_MACRO;
      const replaceVal = `${TTCLID_COMMON}${randomString(randomClickIdLength)}`;

      const { url: path, query, fragmentIdentifier } = parseUrl(urlString, { parseFragmentIdentifier: true });
      const newQuery: any = cloneDeep(query);

      const queryList = Object.keys(newQuery).filter(key => Boolean(key));
      const hasReplacedVal = queryList.some(item => newQuery[item] === replacedVal);
      const getOriginIndex = param => urlString.indexOf(param);
      const getOutputQuery = () =>
        Object.keys(newQuery)
          .filter(key => Boolean(key))
          .sort((a, b) => getOriginIndex(a) - getOriginIndex(b))
          .map(key => `${String(key)}=${newQuery[key] || ''}`)
          .join('&');
      const shellParam = fragmentIdentifier ? `#${fragmentIdentifier}` : '';

      // 参数包含 __CLICKID__
      if (hasReplacedVal) {
        queryList.map(item => {
          // 符合 xxx=__CLICKID__ :替换url参数
          if (newQuery[item] === replacedVal) {
            newQuery[item] = replaceVal;
          }
        });

        const hasTtclid = queryList.some(key => key === paramName);

        // 重新拼接参数
        return hasTtclid
          ? `${path}?${getOutputQuery()}${shellParam}`
          : `${path}?${getOutputQuery()}&${paramName}=${replaceVal}${shellParam}`;
      }

      // 不符合可能是没有__CLICKID__,这种情况需要拼接ttclid
      let addQuery = '';
      if (queryList.length) {
        addQuery = `${getOutputQuery()}&${paramName}=${replaceVal}`;
      } else {
        addQuery = `${paramName}=${replaceVal}`;
      }

      return `${path}?${addQuery}${shellParam}`;
    };

    // 字符串转base64
    const encode = url => {
      const encodedUrl = encodeURI(url);
      const base64 = btoa(encodedUrl);
      return base64;
    };

    const checkNeedAttach = async url => {
      const base64Url = encode(url);
      try {
        const blacklistMap = await checkClickID({ urls: [base64Url] });
        // 1：未命中黑名单，2：命中黑名单, 3: 无效的url
        const isBlackList = blacklistMap?.[base64Url] === 2 || blacklistMap?.[base64Url] === 3;
        return !isBlackList;
      } catch (err) {
        // 后台接口失败，也当作黑名单处理，不要拼接clickid
        return false;
      }
    };

    const handleUrlPreview = async () => {
      if (disabledPreview.value) {
        return;
      }

      let previewUrl = __externalUrl.value;

      // 后台根据黑名单来判断是否需要自动拼接clickid
      const needAttach = await checkNeedAttach(previewUrl);

      if (needAttach) {
        // 需要将url的ttclid值替换为固定长度的随机数
        previewUrl = replaceUrlParam(previewUrl);
      }

      window.open(previewUrl, '_blank');
    };

    const handleSelect = (item: { value: string }) => {
      __externalUrl.value = item.value;
      emit('select', item);
    };

    const handleBlur = () => {
      emit('blur', __externalUrl.value);
      window?.setTimeout(() => {
        // v-model.trim会导致vuex数据正确，但是因为响应式没有捕捉到值的变更，导致组件UI不刷新，这里强制刷新，兼容下拉框选中，延迟更新
        urlInputKey.value++;
        // 放最后面
        onInputTooltipBlur();
      }, 500);
    };

    return {
      name,
      handleFetchSuggestions,
      handleSelect,
      handleBlur,
      handleFocus,
      __externalUrl,
      urlInputKey,
      disabledPreview,
      isShowToolTip,
      handleUrlPreview,
      validateUrl,
      replaceUrlParam,
      isLoading,
      checkNeedAttach,
    };
  },
});
